
export default {
    inject: ['EventBus', 'PIXIWrapper'],

    props: {
        x: {
            type: Number,
            default: 0
        },
        y: {
            type: Number,
            default: 0
        },
        anchorX: {
            type: Number,
            default: 0
        },
        anchorY: {
            type: Number,
            default: 0
        },
        scale: {
            type: Number,
            default: 1
        },
        alpha: {
            type: Number,
            default: 1
        },
        angle: {
            type: Number,
            default: 0
        },
        imagePath: {
            type: String,
            required: true
        },
        animation: {
            type: String,
            default: ''
        },
        animationDelay: {
            type: Number,
            default: 0
        },
        width: {
            type: Number
        },
        height: {
            type: Number
        },
        zIndex: {
            type: Number
        },
        interactive: {
            type: Boolean,
            default: true
        },
        pointer: {
            type: Boolean,
            default: false
        },
        draggable: {
            type: Boolean,
            default: false
        },
        showDelay: {
            type: Number,
            default: 0
        }
    },

    data () {
        return {
            loader: null,
            sprite: null,
            container: null,
            dragging: false,
            dragPosition: {
                pageX: 0,
                pageY: 0
            },
            animationFunction: null,
            animationDelayTimeout: null
        };
    },

    computed: {
        fullImagePath () {
            return this.$helper.assetUrl + this.imagePath;
        }
    },

    watch: {
        alpha () {
            this.sprite.alpha = this.alpha;
        },
        x () {
            this.sprite.x = this.x;
        },
        y () {
            this.sprite.y = this.y;
        },
        pointer () {
            this.sprite.cursor = (this.pointer) ? 'pointer' : '';
        },
        angle () {
            this.sprite.angle = this.angle;
        },
        scale () {
            this.sprite.scale.set(this.scale);
        },
        interactive () {
            this.sprite.interactive = this.interactive;
        },
        zIndex () {
            this.sprite.zIndex = this.zIndex;
        },
        fullImagePath () {
            this.container = this.$game.getParentPIXIContainer(this);
            this.PIXIWrapper.LoaderManager.add(this, () => {
                if (this.sprite !== null && this.PIXIWrapper.LoaderManager.loader.resources[this.fullImagePath]) {
                    this.sprite.texture = this.PIXIWrapper.LoaderManager.loader.resources[this.fullImagePath].texture;
                    this.sprite.texture.baseTexture.mipmap = true;
                }
            }, this.fullImagePath);
        },
        animation () {
            if (this.animation !== '') {
                this.addAnimation();
            }

            if (this.animation === '' && this.animationFunction) {
                this.PIXIWrapper.AnimationManager.remove(this.animationFunction);
            }
        }
    },

    created () {
        this.container = this.$game.getParentPIXIContainer(this);
        this.PIXIWrapper.LoaderManager.add(this, this.draw, this.fullImagePath);
    },

    destroyed () {
        clearTimeout(this.animationDelayTimeout);
        if (this.animationFunction) {
            this.PIXIWrapper.AnimationManager.remove(this.animationFunction);
        }

        if (this.sprite !== null && !this.sprite._destroyed) {
            this.sprite.destroy();
        }
    },

    methods: {
        /**
         * Draw the container.
         */
        draw () {
            let texture = this.PIXIWrapper.LoaderManager.loader.resources[this.fullImagePath].texture;
            texture.baseTexture.mipmap = true;

            this.sprite = new this.PIXIWrapper.PIXI.Sprite(texture);

            this.sprite.x = this.x;
            this.sprite.y = this.y;

            if (this.showDelay !== 0) {
                this.sprite.alpha = 0;

                setTimeout(() => {
                    this.sprite.alpha = this.alpha;
                }, this.$game.delay * this.showDelay);
            } else {
                this.sprite.alpha = this.alpha;
            }

            this.sprite.angle = this.angle;

            this.sprite.scale.set(this.scale);

            if (this.width !== undefined) {
                this.sprite.width = this.width;
            }

            if (this.height !== undefined) {
                this.sprite.height = this.height;
            }

            if (this.zIndex !== undefined) {
                this.sprite.zIndex = this.zIndex;
            }

            this.sprite.anchor.set(this.anchorX, this.anchorY);

            if (this.interactive) {
                this.defineEvents();
            }

            if (this.draggable) {
                this.addDraggable();
            }

            if (this.pointer) {
                this.sprite.cursor = 'pointer';
            }

            if (this.animation !== '') {
                this.addAnimation();
            }

            // Add the sprite to the parent container or the root app stage.
            if (this.container) {
                this.container.addChild(this.sprite);
            } else {
                this.PIXIWrapper.app.stage.addChild(this.sprite);
            }
        },

        /**
         * Define all pixi events that should be emitted ($emit) to the parent component.
         * Only events that are used in parent component ($listeners) will be attached.
         */
        defineEvents () {
            let eventList = [
                'added',
                'click',
                'mousedown',
                'mousemove',
                'mouseout',
                'mouseover',
                'mouseup',
                'mouseupoutside',
                'pointercancel',
                'pointerdown',
                'pointermove',
                'pointerout',
                'pointerover',
                'pointertap',
                'pointerup',
                'pointerupoutside',
                'removed',
                'rightclick',
                'rightdown',
                'rightup',
                'rightupoutside',
                'tap',
                'touchcancel',
                'touchend',
                'touchendoutside',
                'touchmove',
                'touchstart'
            ];

            this.sprite.interactive = true;

            eventList
                .filter(eventName => Object.keys(this.$listeners).includes(eventName))
                .forEach(eventName => {
                    this.sprite.on(eventName, (event) => {
                        this.$emit(eventName, {
                            element: this.sprite,
                            event,
                            AnimationManager: this.PIXIWrapper.AnimationManager
                        });
                    });
                });
        },

        /**
         * Make sprite draggable.
         */
        addDraggable () {
            this.sprite.on('pointerdown', (event) => {
                let pageX = (event.data.originalEvent.touches && event.data.originalEvent.touches.length ? event.data.originalEvent.touches[0].pageX : event.data.originalEvent.pageX);
                let pageY = (event.data.originalEvent.touches && event.data.originalEvent.touches.length ? event.data.originalEvent.touches[0].pageY : event.data.originalEvent.pageY);

                this.dragPosition.pageX = pageX;
                this.dragPosition.pageY = pageY;

                this.dragging = true;
                this.sprite.alpha = 0.8;
                this.$emit('dragstart');
            });

            this.container.on('pointerup', (event) => {
                if (this.dragging) {
                    this.$emit('dragend', {
                        element: this.sprite,
                        x: this.sprite.x,
                        y: this.sprite.y,
                        AnimationManager: this.PIXIWrapper.AnimationManager
                    });

                    this.dragging = false;
                    this.sprite.alpha = 1;
                }
            });

            this.sprite.on('pointermove', (event) => {
                if (this.dragging) {
                    let pageX = (event.data.originalEvent.touches && event.data.originalEvent.touches.length ? event.data.originalEvent.touches[0].pageX : event.data.originalEvent.pageX);
                    let pageY = (event.data.originalEvent.touches && event.data.originalEvent.touches.length ? event.data.originalEvent.touches[0].pageY : event.data.originalEvent.pageY);

                    let movementX = pageX - this.dragPosition.pageX;
                    let movementY = pageY - this.dragPosition.pageY;

                    let newX = this.sprite.x + movementX / this.PIXIWrapper.sizes.scaleFactorX;
                    let newY = this.sprite.y + movementY / this.PIXIWrapper.sizes.scaleFactorY;

                    if (newX <= this.PIXIWrapper.sizes.gameWidth - this.sprite.width && newX >= 0) {
                        this.sprite.x = newX;
                    }

                    if (newY <= this.PIXIWrapper.sizes.gameHeight - this.sprite.height && newY >= 0) {
                        this.sprite.y = newY;
                    }

                    if (Object.keys(this.$listeners).includes('dragmove')) {
                        this.$emit('dragmove', {
                            element: this.sprite,
                            x: this.sprite.x,
                            y: this.sprite.y,
                            AnimationManager: this.PIXIWrapper.AnimationManager
                        });
                    }

                    this.dragPosition.pageX = pageX;
                    this.dragPosition.pageY = pageY;
                }
            });
        },

        /**
         * Add sprite animation.
         */
        addAnimation () {
            this.animationDelayTimeout = setTimeout(() => {
                if (this.animation === 'pulse' || this.animation === 'pulse-fast') {
                    let scaleUp = true;
                    let scaleValue = (this.animation === 'pulse' ? 0.008 : 0.016) * this.scale;

                    this.animationFunction = (delta) => {
                        this.sprite.scale.x += scaleUp ? scaleValue : -scaleValue;
                        this.sprite.scale.y += scaleUp ? scaleValue : -scaleValue;

                        if (this.sprite.scale.x >= 1.15 * this.scale) {
                            scaleUp = false;
                        } else if (this.sprite.scale.x <= 1 * this.scale) {
                            scaleUp = true;
                        }
                    };

                    this.PIXIWrapper.AnimationManager.add(this.animationFunction);
                }
            }, this.$game.delay * this.animationDelay);
        }
    },

    render: (h) => h()
};
